import React, { useEffect, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Card,
  Table,
  Tag,
  Select,
  DatePicker,
  Button,
  Space,
  Row,
  Col,
  Statistic,
  Typography,
} from "antd";
import { FileTextOutlined } from "@ant-design/icons";
import moment from "moment";
import { getPurchaseOrderReports } from "../../../redux/actions/purchase/purchaseOrderAction";
import { loadAllSuppliersForSearch } from "../../../redux/actions/supplier/getSuppliersAction";
import { CACHE_KEYS, getCachedDateFilter, setCachedDateFilter } from "../../../utils/dateFilterCache";

const { Title } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;

const PurchaseOrderReports = () => {
  const dispatch = useDispatch();
  const [reportType, setReportType] = useState("pending-items");
  const [filters, setFilters] = useState(() => {
    const c = getCachedDateFilter(CACHE_KEYS.PURCHASE_ORDER_REPORTS);
    return {
      supplier_id: "",
      start_date: c?.startdate ?? "",
      end_date: c?.enddate ?? "",
    };
  });

  const reports = useSelector((state) => state.purchaseOrders.reports);
  const allSuppliers = useSelector((state) => state.suppliers.searchList || state.suppliers.list) || [];

  useEffect(() => {
    dispatch(loadAllSuppliersForSearch({ status: true }));
  }, [dispatch]);

  const loadReport = useCallback(() => {
    const reportFilters = {};
    if (filters.supplier_id) reportFilters.supplier_id = filters.supplier_id;
    if (filters.start_date) reportFilters.start_date = filters.start_date;
    if (filters.end_date) reportFilters.end_date = filters.end_date;
    dispatch(getPurchaseOrderReports(reportType, reportFilters));
  }, [dispatch, reportType, filters]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const handleDateRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      const start_date = dates[0].format("YYYY-MM-DD");
      const end_date = dates[1].format("YYYY-MM-DD");
      setCachedDateFilter(CACHE_KEYS.PURCHASE_ORDER_REPORTS, { startdate: start_date, enddate: end_date });
      setFilters((prev) => ({ ...prev, start_date, end_date }));
    } else {
      setFilters((prev) => ({ ...prev, start_date: "", end_date: "" }));
    }
  };

  const pendingItemsColumns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
    },
    {
      title: "Order Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      render: (supplier) => supplier?.name || "-",
    },
    {
      title: "Product",
      dataIndex: "product",
      key: "product",
      render: (product) => product?.name || "-",
    },
    {
      title: "Ordered",
      dataIndex: "ordered_quantity",
      key: "ordered_quantity",
    },
    {
      title: "Received",
      dataIndex: "received_quantity",
      key: "received_quantity",
    },
    {
      title: "Pending",
      dataIndex: "pending_quantity",
      key: "pending_quantity",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "received" ? "green" : status === "partial" ? "blue" : "orange"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
  ];

  const completionColumns = [
    {
      title: "Order Number",
      dataIndex: "order_number",
      key: "order_number",
    },
    {
      title: "Order Date",
      dataIndex: "order_date",
      key: "order_date",
      render: (date) => moment(date).format("DD-MM-YYYY"),
    },
    {
      title: "Supplier",
      dataIndex: "supplier",
      key: "supplier",
      render: (supplier) => supplier?.name || "-",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <Tag color={status === "received" ? "green" : status === "partial" ? "blue" : "orange"}>
          {status.toUpperCase()}
        </Tag>
      ),
    },
    {
      title: "Total Amount",
      dataIndex: "total_amount",
      key: "total_amount",
      render: (amount) => `₹${parseFloat(amount || 0).toFixed(2)}`,
    },
    {
      title: "Ordered Qty",
      dataIndex: "total_ordered",
      key: "total_ordered",
    },
    {
      title: "Received Qty",
      dataIndex: "total_received",
      key: "total_received",
    },
    {
      title: "Completion %",
      dataIndex: "completion_percentage",
      key: "completion_percentage",
      render: (percentage) => `${percentage}%`,
    },
    {
      title: "Pending Qty",
      dataIndex: "pending_quantity",
      key: "pending_quantity",
    },
  ];

  const renderReport = () => {
    const reportData = reports?.[reportType];

    if (reportType === "pending-items") {
      return (
        <Table
          columns={pendingItemsColumns}
          dataSource={reportData?.pendingItems || []}
          rowKey={(record, index) => `${record.order_id}-${record.product?.id}-${index}`}
          pagination={{ pageSize: 20 }}
        />
      );
    }

    if (reportType === "completion") {
      return (
        <Table
          columns={completionColumns}
          dataSource={reportData?.completionReport || []}
          rowKey="order_id"
          pagination={{ pageSize: 20 }}
        />
      );
    }

    if (reportType === "status-summary") {
      const summary = reportData?.summary || {};
      return (
        <Row gutter={16}>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Pending"
                value={summary.pending || 0}
                prefix={<Tag color="orange">P</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Partial"
                value={summary.partial || 0}
                prefix={<Tag color="blue">P</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Received"
                value={summary.received || 0}
                prefix={<Tag color="green">R</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={12} md={6}>
            <Card>
              <Statistic
                title="Cancelled"
                value={summary.cancelled || 0}
                prefix={<Tag color="red">C</Tag>}
              />
            </Card>
          </Col>
          <Col xs={24} sm={24} md={24} style={{ marginTop: "16px" }}>
            <Card>
              <Statistic
                title="Total Orders"
                value={summary.total || 0}
                prefix={<FileTextOutlined />}
              />
            </Card>
          </Col>
        </Row>
      );
    }

    return null;
  };

  return (
    <div style={{ padding: "20px" }}>
      <Card>
        <Title level={3}>Purchase Order Reports</Title>

        <Space style={{ marginBottom: "20px", flexWrap: "wrap" }}>
          <Select
            value={reportType}
            onChange={setReportType}
            style={{ width: 200 }}
          >
            <Option value="pending-items">Pending Items</Option>
            <Option value="completion">Completion Report</Option>
            <Option value="status-summary">Status Summary</Option>
          </Select>

          <Select
            placeholder="Filter by Supplier"
            style={{ width: 200 }}
            allowClear
            value={filters.supplier_id || undefined}
            onChange={(value) =>
              setFilters((prev) => ({ ...prev, supplier_id: value || "" }))
            }
          >
            {allSuppliers.map((supplier) => (
              <Option key={supplier.id} value={supplier.id}>
                {supplier.name}
              </Option>
            ))}
          </Select>

          <RangePicker
            onChange={handleDateRangeChange}
            format="DD-MM-YYYY"
          />

          <Button onClick={loadReport}>Refresh</Button>
        </Space>

        {renderReport()}
      </Card>
    </div>
  );
};

export default PurchaseOrderReports;
